import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Collapse from "react-bootstrap/Collapse";
import Swal from "sweetalert2";
import Select, { components } from "react-select";

import AttendanceServices from "../../../services/AttendanceServices";
import ReviewAttendanceModal from "./ReviewAttendanceModal";
import ReviewDieselModal from "./ReviewDieselModal";

const dateKeyFormatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
});

const getISTDateKey = (value) => {
    if (!value) return "";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "";

    const parts = dateKeyFormatter.formatToParts(date);

    const getPart = (type) =>
        parts.find((part) => part.type === type)?.value || "";

    return `${getPart("year")}-${getPart("month")}-${getPart("day")}`;
};

const formatAttendanceDate = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleDateString("en-GB", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "short",
        year: "numeric"
    });
};

const AttendanceFilterOption = (props) => (
    <components.Option {...props}>
        <span className="d-flex align-items-center gap-2">
            <input
                type="checkbox"
                checked={props.isSelected}
                readOnly
                tabIndex={-1}
                className="pe-none"
                aria-hidden="true"
            />
            <span>{props.label}</span>
        </span>
    </components.Option>
);

const getOptions = (records, getValue, getLabel) => {
    const unique = new Map();

    records.forEach((item) => {
        const value = String(getValue(item) || "");

        if (!value) return;

        unique.set(value, {
            value,
            label: getLabel(item) || value
        });
    });

    return [...unique.values()].sort((a, b) =>
        a.label.localeCompare(b.label)
    );
};

const getStoreId = (item) =>
    String(
        item.store?._id ||
        item.attendanceStoreId ||
        ""
    );

const getDriverId = (item) =>
    String(
        item.driver?._id ||
        (typeof item.driver === "string" ? item.driver : "")
    );

const statusLabels = {
    pending: "Pending",
    approved: "Approved",
    rejected: "Rejected"
};

const statusOptions = Object.entries(statusLabels).map(
    ([value, label]) => ({
        value,
        label
    })
);

const rowOptions = [5, 10, 20, 50, 100].map((value) => ({
    value,
    label: `${value} Rows`
}));

const ApprovalBadge = ({ status }) => {
    const value = status || "pending";

    const color =
        value === "approved"
            ? "bg-success"
            : value === "rejected"
                ? "bg-danger"
                : "bg-warning text-dark";

    return (
        <span className={`badge ${color}`}>
            {statusLabels[value] || value}
        </span>
    );
};

const AttendanceMultiFilter = ({
    options,
    selected,
    onChange,
    placeholder,
    disabled,
    inputId
}) => (
    <Select
        inputId={inputId}
        isMulti
        isSearchable
        isClearable
        options={options}
        value={options.filter((option) =>
            selected.includes(option.value)
        )}
        onChange={(values) =>
            onChange(
                (values || []).map((option) => option.value)
            )
        }
        components={{
            Option: AttendanceFilterOption
        }}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        blurInputOnSelect={false}
        placeholder={placeholder}
        isDisabled={disabled}
        className="custom-react-select attendance-select-medium"
        classNamePrefix="attendance-filter"
        menuPlacement="auto"
        aria-label={placeholder}
    />
);

const PendingAttendance = () => {
    const [open, setOpen] = useState(true);
    const [open2, setOpen2] = useState(true);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [data, setData] = useState([]);
    const [run, setRun] = useState(0);

    const [sort, setSort] = useState(20);
    const [page, setPage] = useState(0);

    const [searchTerm, setSearchTerm] = useState("");
    const [filterResetKey, setFilterResetKey] = useState(0);
    const [selectedStores, setSelectedStores] = useState([]);
    const [selectedDrivers, setSelectedDrivers] = useState([]);
    const [selectedStatuses, setSelectedStatuses] = useState([]);

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const [selectedAttendance, setSelectedAttendance] =
        useState(null);

    const [showApprovalModal, setShowApprovalModal] =
        useState(false);

    const [showDieselModal, setShowDieselModal] =
        useState(false);

    useEffect(() => {
        let cancelled = false;

        const fetchPendingReviews = async () => {
            setLoading(true);
            setError("");

            try {
                const response =
                    await AttendanceServices.getPendingReviews();

                if (!Array.isArray(response?.data)) {
                    throw new Error(
                        "Unexpected attendance API response."
                    );
                }

                if (!cancelled) {
                    setData(response.data);
                }
            } catch (requestError) {
                if (cancelled) return;

                const message =
                    requestError?.response?.data?.message ||
                    requestError?.message ||
                    "Failed to load pending reviews.";

                setError(message);
                setData([]);

                Swal.fire("Error", message, "error");
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchPendingReviews();

        return () => {
            cancelled = true;
        };
    }, [run]);

    const storeOptions = useMemo(
        () =>
            getOptions(
                data,
                getStoreId,
                (item) =>
                    item.store?.name ||
                    item.attendanceStore
            ),
        [data]
    );

    const driverOptions = useMemo(
        () =>
            getOptions(
                data,
                getDriverId,
                (item) =>
                    item.driver?.name ||
                    item.driverName
            ),
        [data]
    );

    const invalidDateRange = Boolean(
        fromDate &&
        toDate &&
        fromDate > toDate
    );

    const filteredSearch = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();

        if (invalidDateRange) return [];

        return data.filter((item) => {
            if (
                selectedStores.length > 0 &&
                !selectedStores.includes(getStoreId(item))
            ) {
                return false;
            }

            if (
                selectedDrivers.length > 0 &&
                !selectedDrivers.includes(getDriverId(item))
            ) {
                return false;
            }

            const attendanceStatus =
                item.approvalStatus || "pending";

            const dieselStatus =
                item.dieselApprovalStatus || "pending";

            // Match either approval status.
            if (
                selectedStatuses.length > 0 &&
                ![attendanceStatus, dieselStatus].some(
                    (status) =>
                        selectedStatuses.includes(status)
                )
            ) {
                return false;
            }

            if (fromDate || toDate) {
                const attendanceStart =
                    getISTDateKey(item.startTime);

                const attendanceEnd =
                    getISTDateKey(item.endTime);

                if (!attendanceStart || !attendanceEnd) {
                    return false;
                }

                if (attendanceEnd < attendanceStart) {
                    return false;
                }

                // Include attendance periods overlapping
                // the selected dates, inclusive of both dates.
                if (
                    fromDate &&
                    attendanceEnd < fromDate
                ) {
                    return false;
                }

                if (
                    toDate &&
                    attendanceStart > toDate
                ) {
                    return false;
                }
            }

            const searchableText = [
                item.driver?.name,
                item.driverName,
                item.store?.name,
                item.attendanceStore,
                item.vehicle?.asset?.masterItem?.partNo,
                item.vehicle?.asset?.masterItem?.description,
                item.vehicle?.asset?.equipmentNo,
                item.vehicleName,
                item.equipmentNo,
                attendanceStatus,
                dieselStatus
            ]
                .filter(
                    (value) =>
                        value !== null &&
                        value !== undefined
                )
                .join(" ")
                .toLowerCase();

            return (
                !term ||
                searchableText.includes(term)
            );
        });
    }, [
        data,
        searchTerm,
        selectedStores,
        selectedDrivers,
        selectedStatuses,
        fromDate,
        toDate,
        invalidDateRange
    ]);

    const pagination = Math.ceil(
        filteredSearch.length / sort
    );

    const currentPage = Math.min(
        page,
        Math.max(0, pagination - 1)
    );

    const start = currentPage * sort;

    const visibleEntries = filteredSearch.slice(
        start,
        start + sort
    );

    const pendingAttendanceCount =
        filteredSearch.filter(
            (item) =>
                (item.approvalStatus || "pending") ===
                "pending"
        ).length;

    const pendingDieselCount =
        filteredSearch.filter(
            (item) =>
                (item.dieselApprovalStatus || "pending") ===
                "pending"
        ).length;

    const changeFilter = (setter) => (value) => {
        setter(value);
        setPage(0);
    };

    const resetAll = () => {
        setSearchTerm("");
        setSelectedStores([]);
        setSelectedDrivers([]);
        setSelectedStatuses([]);
        setFromDate("");
        setToDate("");
        setSort(20);
        setPage(0);

        // Clear React Select's internal search text as well.
        setFilterResetKey((previous) => previous + 1);
    };

    const closeAttendanceModal = () => {
        setShowApprovalModal(false);
        setSelectedAttendance(null);
    };

    const closeDieselModal = () => {
        setShowDieselModal(false);
        setSelectedAttendance(null);
    };

    return (
        <>
            <ReviewAttendanceModal
                show={showApprovalModal}
                handleClose={closeAttendanceModal}
                attendance={selectedAttendance}
                setRun={setRun}
            />

            <ReviewDieselModal
                show={showDieselModal}
                handleClose={closeDieselModal}
                attendance={selectedAttendance}
                setRun={setRun}
            />

            <div className="row">
                <div className="col-xl-12">
                    <div className="filter cm-content-box box-primary">
                        <div className="content-title">
                            <div className="cpa">
                                <i className="fas fa-filter me-2" />
                                Filter
                            </div>

                            <div className="tools">
                                <Link
                                    to="#"
                                    className={`SlideToolHeader ${open ? "collapse" : "expand"
                                        }`}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        setOpen(
                                            (previous) => !previous
                                        );
                                    }}
                                    aria-label="Toggle filters"
                                    aria-expanded={open}
                                >
                                    <i className="fas fa-angle-up" />
                                </Link>
                            </div>
                        </div>

                        <Collapse in={open}>
                            <div className="cm-content-body form excerpt">
                                <div
                                    key={filterResetKey}
                                    className="card-body attendance-filter-form"
                                >
                                    <div className="row g-2 align-items-end">
                                        <div className="col-12 col-md-6 col-xl-3">
                                            <label
                                                htmlFor="attendance-search"
                                                className="form-label mb-1"
                                            >
                                                Search
                                            </label>

                                            <input
                                                id="attendance-search"
                                                type="text"
                                                className="form-control"
                                                placeholder="Search machine / driver / store"
                                                value={searchTerm}
                                                disabled={loading}
                                                onChange={(event) => {
                                                    setSearchTerm(
                                                        event.target.value
                                                    );
                                                    setPage(0);
                                                }}
                                            />
                                        </div>

                                        <div className="col-12 col-md-6 col-xl-2">
                                            <label
                                                htmlFor="attendance-store-filter"
                                                className="form-label mb-1"
                                            >
                                                Store
                                            </label>

                                            <AttendanceMultiFilter
                                                inputId="attendance-store-filter"
                                                options={storeOptions}
                                                selected={selectedStores}
                                                onChange={changeFilter(
                                                    setSelectedStores
                                                )}
                                                placeholder="All Stores"
                                                disabled={loading}
                                            />
                                        </div>

                                        <div className="col-12 col-md-6 col-xl-3">
                                            <label
                                                htmlFor="attendance-driver-filter"
                                                className="form-label mb-1"
                                            >
                                                Driver
                                            </label>

                                            <AttendanceMultiFilter
                                                inputId="attendance-driver-filter"
                                                options={driverOptions}
                                                selected={selectedDrivers}
                                                onChange={changeFilter(
                                                    setSelectedDrivers
                                                )}
                                                placeholder="All Drivers"
                                                disabled={loading}
                                            />
                                        </div>

                                        <div className="col-12 col-md-6 col-xl-2">
                                            <label
                                                htmlFor="attendance-status-filter"
                                                className="form-label mb-1"
                                            >
                                                Status
                                            </label>

                                            <AttendanceMultiFilter
                                                inputId="attendance-status-filter"
                                                options={statusOptions}
                                                selected={selectedStatuses}
                                                onChange={changeFilter(
                                                    setSelectedStatuses
                                                )}
                                                placeholder="All Statuses"
                                                disabled={loading}
                                            />
                                        </div>

                                        <div className="col-12 col-md-6 col-xl-2">
                                            <label
                                                htmlFor="attendance-rows"
                                                className="form-label mb-1"
                                            >
                                                Rows
                                            </label>

                                            <Select
                                                inputId="attendance-rows"
                                                options={rowOptions}
                                                value={rowOptions.find(
                                                    (option) =>
                                                        option.value === sort
                                                )}
                                                onChange={(selected) => {
                                                    setSort(
                                                        selected?.value || 20
                                                    );
                                                    setPage(0);
                                                }}
                                                isSearchable={false}
                                                isDisabled={loading}
                                                className="custom-react-select attendance-select-medium"
                                                classNamePrefix="attendance-filter"
                                                menuPlacement="auto"
                                                aria-label="Rows per page"
                                            />
                                        </div>
                                    </div>

                                    <div className="row g-2 justify-content-center align-items-end mt-3">
                                        <div className="col-12 col-sm-5 col-md-3 col-xl-2">
                                            <label
                                                htmlFor="attendance-from-date"
                                                className="form-label mb-1"
                                            >
                                                From Date
                                            </label>

                                            <input
                                                id="attendance-from-date"
                                                type="date"
                                                className="form-control"
                                                value={fromDate}
                                                max={toDate || undefined}
                                                disabled={loading}
                                                onChange={(event) => {
                                                    setFromDate(
                                                        event.target.value
                                                    );
                                                    setPage(0);
                                                }}
                                                aria-invalid={invalidDateRange}
                                            />
                                        </div>

                                        <div className="col-12 col-sm-5 col-md-3 col-xl-2">
                                            <label
                                                htmlFor="attendance-to-date"
                                                className="form-label mb-1"
                                            >
                                                To Date
                                            </label>

                                            <input
                                                id="attendance-to-date"
                                                type="date"
                                                className="form-control"
                                                value={toDate}
                                                min={fromDate || undefined}
                                                disabled={loading}
                                                onChange={(event) => {
                                                    setToDate(
                                                        event.target.value
                                                    );
                                                    setPage(0);
                                                }}
                                                aria-invalid={invalidDateRange}
                                            />
                                        </div>

                                        <div className="col-12 col-sm-auto text-center">
                                            <button
                                                type="button"
                                                className="btn btn-primary attendance-reset-btn"
                                                disabled={loading}
                                                onClick={resetAll}
                                            >
                                                Reset All
                                            </button>
                                        </div>
                                    </div>

                                    {invalidDateRange && (
                                        <div
                                            className="text-danger small text-center mt-2"
                                            role="alert"
                                        >
                                            From Date must be on or before
                                            To Date.
                                        </div>
                                    )}

                                    <div className="text-muted small text-center mt-2">
                                        Status matches either attendance
                                        or diesel approval.
                                    </div>
                                </div>
                            </div>
                        </Collapse>
                    </div>

                    <div className="filter cm-content-box box-primary">
                        <div className="content-title">
                            <div className="cpa">
                                <i
                                    className="fa fa-list me-2"
                                    aria-hidden="true"
                                />

                                Pending Attendance &amp; Diesel Reviews

                                <span className="badge bg-warning ms-2">
                                    {filteredSearch.length}
                                </span>
                            </div>

                            <div className="tools">
                                <Link
                                    to="#"
                                    className={`SlideToolHeader ${open2 ? "collapse" : "expand"
                                        }`}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        setOpen2(
                                            (previous) => !previous
                                        );
                                    }}
                                    aria-label="Toggle reviews table"
                                    aria-expanded={open2}
                                >
                                    <i className="fas fa-angle-up" />
                                </Link>
                            </div>
                        </div>

                        <Collapse in={open2}>
                            <div className="cm-content-body form excerpt">
                                <div className="card-body">
                                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                                        <div className="d-flex flex-wrap gap-2">
                                            <span className="badge bg-success">
                                                Attendance Pending:{" "}
                                                {pendingAttendanceCount}
                                            </span>

                                            <span className="badge bg-primary">
                                                Diesel Pending:{" "}
                                                {pendingDieselCount}
                                            </span>
                                        </div>

                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm"
                                            disabled={loading}
                                            onClick={() =>
                                                setRun(
                                                    (previous) =>
                                                        previous + 1
                                                )
                                            }
                                        >
                                            <i className="fas fa-sync-alt me-1" />
                                            Refresh
                                        </button>
                                    </div>

                                    {error && (
                                        <div
                                            className="alert alert-danger"
                                            role="alert"
                                        >
                                            {error}
                                        </div>
                                    )}

                                    {loading && (
                                        <div
                                            className="text-center mb-3"
                                            role="status"
                                            aria-live="polite"
                                        >
                                            <span
                                                className="spinner-border spinner-border-sm text-primary me-2"
                                                aria-hidden="true"
                                            />
                                            Loading reviews...
                                        </div>
                                    )}

                                    <div className="table-responsive">
                                        <div
                                            id="content_wrapper"
                                            className="dataTables_wrapper no-footer"
                                        >
                                            <table
                                                className="table table-bordered table-responsive-lg table-striped table-condensed flip-content"
                                                id="pending-attendance-data"
                                                aria-busy={loading}
                                            >
                                                <thead>
                                                    <tr>
                                                        <th>S.No</th>
                                                        <th>Machine Details</th>
                                                        <th>Date</th>
                                                        <th>HMR</th>
                                                        <th>Attendance</th>
                                                        <th>Diesel</th>
                                                        <th>Review &amp; Approve</th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {visibleEntries.length > 0 ? (
                                                        visibleEntries.map((item, index) => {
                                                            const asset = item.vehicle?.asset;

                                                            const attendanceReviewed = [
                                                                "approved",
                                                                "rejected"
                                                            ].includes(item.approvalStatus);

                                                            const dieselReviewed = [
                                                                "approved",
                                                                "rejected"
                                                            ].includes(item.dieselApprovalStatus);

                                                            return (
                                                                <tr key={item._id}>
                                                                    <td>{start + index + 1}</td>

                                                                    <td className="text-start">
                                                                        <div className="mb-1">
                                                                            <strong>Store: </strong>
                                                                            {item.store?.name ||
                                                                                item.attendanceStore ||
                                                                                "-"}
                                                                        </div>

                                                                        <div className="mb-1">
                                                                            <strong>Part No: </strong>
                                                                            {asset?.masterItem?.partNo || "-"}
                                                                        </div>

                                                                        <div className="mb-1">
                                                                            <strong>Description: </strong>

                                                                            <span className="scrollable-description">
                                                                                {asset?.masterItem?.description ||
                                                                                    item.vehicleName ||
                                                                                    "-"}
                                                                            </span>
                                                                        </div>

                                                                        <div className="mb-1">
                                                                            <strong>Equipment No: </strong>
                                                                            {asset?.equipmentNo ||
                                                                                item.equipmentNo ||
                                                                                "-"}
                                                                        </div>

                                                                        <div>
                                                                            <strong>Driver: </strong>
                                                                            {item.driver?.name ||
                                                                                item.driverName ||
                                                                                "-"}
                                                                        </div>
                                                                    </td>

                                                                    <td className="text-start text-nowrap">
                                                                        <div className="mb-1">
                                                                            <strong>Start: </strong>
                                                                            {formatAttendanceDate(item.startTime)}
                                                                        </div>

                                                                        <div>
                                                                            <strong>End: </strong>
                                                                            {formatAttendanceDate(item.endTime)}
                                                                        </div>
                                                                    </td>

                                                                    <td className="text-start text-nowrap">
                                                                        <div className="mb-1">
                                                                            <strong>Start: </strong>
                                                                            {item.startKM ?? "-"}
                                                                        </div>

                                                                        <div>
                                                                            <strong>End: </strong>
                                                                            {item.endKM ?? "-"}
                                                                        </div>
                                                                    </td>

                                                                    <td>
                                                                        <ApprovalBadge
                                                                            status={item.approvalStatus}
                                                                        />
                                                                    </td>

                                                                    <td>
                                                                        <ApprovalBadge
                                                                            status={item.dieselApprovalStatus}
                                                                        />
                                                                    </td>

                                                                    <td className="attendance-review-cell">
                                                                        <div className="attendance-review-actions">
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-success attendance-review-icon"
                                                                                disabled={loading}
                                                                                title="Review Attendance"
                                                                                aria-label="Review Attendance"
                                                                                onClick={() => {
                                                                                    setSelectedAttendance(item);
                                                                                    setShowDieselModal(false);
                                                                                    setShowApprovalModal(true);
                                                                                }}
                                                                            >
                                                                                <i
                                                                                    className="fas fa-clipboard-check"
                                                                                    aria-hidden="true"
                                                                                />
                                                                            </button>

                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-primary attendance-review-icon"
                                                                                disabled={loading}
                                                                                title="Review Diesel"
                                                                                aria-label="Review Diesel"
                                                                                onClick={() => {
                                                                                    setSelectedAttendance(item);
                                                                                    setShowApprovalModal(false);
                                                                                    setShowDieselModal(true);
                                                                                }}
                                                                            >
                                                                                <i
                                                                                    className="fas fa-gas-pump"
                                                                                    aria-hidden="true"
                                                                                />
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })
                                                    ) : (
                                                        <tr>
                                                            <td colSpan={7} className="text-center">
                                                                {loading
                                                                    ? "Loading reviews..."
                                                                    : error
                                                                        ? "Unable to load reviews."
                                                                        : invalidDateRange
                                                                            ? "Please select a valid date range."
                                                                            : "No pending reviews found."}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>

                                            <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">
                                                <div className="dataTables_info">
                                                    Showing{" "}
                                                    {filteredSearch.length
                                                        ? start + 1
                                                        : 0}
                                                    {" "}to{" "}
                                                    {Math.min(
                                                        start + sort,
                                                        filteredSearch.length
                                                    )}
                                                    {" "}of{" "}
                                                    {filteredSearch.length}
                                                </div>

                                                <div className="dataTables_paginate paging_simple_numbers">
                                                    <Link
                                                        to="#"
                                                        className={`paginate_button previous ${currentPage === 0 ||
                                                            loading
                                                            ? "disabled"
                                                            : ""
                                                            }`}
                                                        aria-label="Previous page"
                                                        aria-disabled={
                                                            currentPage === 0 ||
                                                            loading
                                                        }
                                                        onClick={(event) => {
                                                            event.preventDefault();

                                                            if (
                                                                !loading &&
                                                                currentPage > 0
                                                            ) {
                                                                setPage(
                                                                    currentPage - 1
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        <i
                                                            className="fa fa-angle-double-left"
                                                            aria-hidden="true"
                                                        />
                                                    </Link>

                                                    <span>
                                                        {Array.from(
                                                            { length: pagination },
                                                            (_, index) => (
                                                                <Link
                                                                    key={index}
                                                                    to="#"
                                                                    className={`paginate_button ${currentPage === index
                                                                        ? "current"
                                                                        : ""
                                                                        }`}
                                                                    aria-current={
                                                                        currentPage === index
                                                                            ? "page"
                                                                            : undefined
                                                                    }
                                                                    aria-disabled={loading}
                                                                    onClick={(event) => {
                                                                        event.preventDefault();

                                                                        if (!loading) {
                                                                            setPage(index);
                                                                        }
                                                                    }}
                                                                >
                                                                    {index + 1}
                                                                </Link>
                                                            )
                                                        )}
                                                    </span>

                                                    <Link
                                                        to="#"
                                                        className={`paginate_button next ${currentPage >= pagination - 1 ||
                                                            loading
                                                            ? "disabled"
                                                            : ""
                                                            }`}
                                                        aria-label="Next page"
                                                        aria-disabled={
                                                            currentPage >= pagination - 1 ||
                                                            loading
                                                        }
                                                        onClick={(event) => {
                                                            event.preventDefault();

                                                            if (
                                                                !loading &&
                                                                currentPage < pagination - 1
                                                            ) {
                                                                setPage(
                                                                    currentPage + 1
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        <i
                                                            className="fa fa-angle-double-right"
                                                            aria-hidden="true"
                                                        />
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Collapse>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PendingAttendance;